import type { ConnectDragSource } from 'react-dnd';

import copy from 'clipboard-copy';
import GithubSlugger from 'github-slugger';
import React, { useState, useRef, useCallback } from 'react';
import { type Range, type Element } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';

import { useSuperHubStore } from '@core/store';

import type { DropdownRef } from '@ui/Dropdown';
import Dropdown from '@ui/Dropdown';
import Icon from '@ui/Icon';
import { ReusableContent, Heading } from '@ui/MarkdownEditor/editor/blocks';
import Menu, { MenuDivider, MenuItem } from '@ui/Menu';
import Tooltip from '@ui/Tooltip';

import Handle from './Handle';
import Hint from './Hint';
import classes from './style.module.scss';
import Utils, { expandSelection } from './utils';

const preventDefault = (e: { preventDefault: () => void }) => e.preventDefault();

// prevent block menu from being cut off by page header
const POPPER_OPTIONS = {
  popperOptions: {
    modifiers: [
      {
        name: 'flip',
        options: {
          padding: { top: 100 },
        },
      },
    ],
  },
};

const slugger = new GithubSlugger();

const BlockMenuMenu = ({ element, drag }: { drag: ConnectDragSource; element: Element | null }) => {
  const editor = useSlateStatic();
  const dropdownRef = useRef<DropdownRef>(null);
  const [selection, setSelection] = useState<Range | null>(null);
  const path = element && ReactEditor.findPath(editor, element);
  const isFirstElement = path && path[0] === 0;
  const isReusableContent = element && ReusableContent.isReusableContent(element);
  const isHeading = element && Heading.isHeading(element);
  const baseUrl = editor.props.projectBaseUrl;
  const [slug, routeSection] = useSuperHubStore(s => [s.slug, s.routeSection]);

  const handleCopy = useCallback(() => {
    if (!selection) {
      // eslint-disable-next-line no-console
      console.warn('No selection!?');
      return;
    }

    Utils.copy(editor, selection);
    dropdownRef?.current?.toggle(false);
  }, [editor, selection]);

  const handleDelete = useCallback(() => {
    if (!selection) {
      // eslint-disable-next-line no-console
      console.warn('No selection!?');
      return;
    }

    Utils.remove(editor, selection);
  }, [editor, selection]);

  const insertLineBelow = useCallback(() => {
    if (!selection) {
      // eslint-disable-next-line no-console
      console.warn('No selection!?');
      return;
    }

    Utils.insertLineBelow(editor, selection);
    dropdownRef?.current?.toggle(false);
  }, [editor, selection]);

  const insertLineAbove = useCallback(() => {
    if (!selection) {
      // eslint-disable-next-line no-console
      console.warn('No selection!?');
      return;
    }

    Utils.insertLineAbove(editor);
    dropdownRef?.current?.toggle(false);
  }, [editor, selection]);

  const makeReusable = useCallback(() => {
    if (!selection) {
      // eslint-disable-next-line no-console
      console.warn('No selection!?');
      return;
    }

    ReusableContent.operations.makeReusable(editor, selection);
    dropdownRef?.current?.toggle(false);
  }, [editor, selection]);

  const handleCopyHeadingLink = useCallback(() => {
    if (!selection) {
      // eslint-disable-next-line no-console
      console.warn('No selection!?');
      return;
    }

    // construct URL to heading
    const headingText = editor.serialize(element).replace(/^#+ ?/, '');
    const headingSlug = slugger.slug(headingText);
    const headingLink = `${baseUrl}${routeSection}/${slug}#${headingSlug}`;

    copy(headingLink);

    dropdownRef?.current?.toggle(false);
  }, [baseUrl, editor, element, routeSection, selection, slug]);

  const onToggle = useCallback(
    (value: boolean) => {
      if (!element) return;

      if (value === false || !path) return;

      expandSelection(editor, path);
      ReactEditor.focus(editor);
      setSelection(editor.selection);

      // @note: we defer the close till after a full render-cycle so the
      // toolbar has a chance to respond to the selection change. Maybe there's
      // a more deterministic way to do this?
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          editor.inlineToolbar.close();
        });
      });
    },
    [editor, element, path],
  );

  return (
    <Dropdown ref={dropdownRef} align="top" justify="start" onToggle={onToggle} tippyOptions={POPPER_OPTIONS}>
      {/* @note: Adding a div here to accept a ref from the Dropdown, so we don't need to forward that through the Tooltip */}
      <div>
        <Tooltip asTitle content={<Hint />} placement="bottom">
          <Handle drag={drag} isReusableContent={!!isReusableContent} />
        </Tooltip>
      </div>
      <Menu className={classes.BlockMenu_Menu}>
        <MenuItem
          icon={<Icon name="copy" />}
          onClick={handleCopy}
          /* @ts-ignore */
          onMouseDown={preventDefault}
        >
          Copy
        </MenuItem>
        {!!isHeading && (
          <MenuItem
            icon={<Icon name="anchor" />}
            onClick={handleCopyHeadingLink}
            /* @ts-ignore */
            onMouseDown={preventDefault}
          >
            Copy Link to Heading
          </MenuItem>
        )}
        {!!isFirstElement && (
          <MenuItem
            icon={<Icon name="corner-up-right" />}
            onClick={insertLineAbove}
            /* @ts-ignore */
            onMouseDown={preventDefault}
          >
            Add Line Above
          </MenuItem>
        )}
        <MenuItem
          icon={<Icon name="corner-down-right" />}
          onClick={insertLineBelow}
          /* @ts-ignore */
          onMouseDown={preventDefault}
        >
          Add Line Below
        </MenuItem>
        {!!editor.props.useReusableContent && !isReusableContent && !Utils.containsReusableContent(editor) && (
          <MenuItem
            icon={<Icon name="recycle" />}
            onClick={makeReusable}
            /* @ts-ignore */
            onMouseDown={preventDefault}
          >
            Make Reusable
          </MenuItem>
        )}
        <MenuDivider />
        <MenuItem
          color="red"
          icon={<Icon name="trash" />}
          onClick={handleDelete}
          /* @ts-ignore */
          onMouseDown={preventDefault}
        >
          Delete
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};

export default BlockMenuMenu;
